import {
  Typography,
  Paper,
  Button,
  IconButton,
  MenuItem,
  Menu,
  CircularProgress,
} from '@mui/material';
import Page from '../../../../components/page/Page';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useArticlesCore } from '../../../../../core/modules/articles/index';
import { useCallback, useEffect, useState } from 'react';
import Header from '../../../../components/administration/articles/Header';
import { useMasterCore } from '../../../../../core/modules/master';
import { Article } from '../../../../../core/interfaces/article.interface';
import PrintIcon from '@mui/icons-material/Print';
import { useMeCore } from '../../../../../core/modules/me';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useCompanyCore } from '../../../../../core/modules/company';
import Barcode from 'react-barcode';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyButton from '../../../../components/shared/CopyButton';
import { usePrintLabel } from '../../../../../core/hooks/usePrintLabel';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const Articles = () => {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    noMore,
    articles,
    options,
    increasePage,
    getArticles,
    getArticle,
    setArticle,
    resetOptions,
    clearError,
    setOptions,
  } = useArticlesCore();
  const { goToWelcome, goToAddArticle, goToEditArticle } = useNavigationCore();
  const { printArticle } = usePrintLabel();
  
  // Hook para obtener categorías y tipos de producto
  const {
    productTypes,
    productCategories,
    getTypes,
    getCategories,
  } = useMasterCore();

  const [forEditing, setForEditing] = useState<Article | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [initialSearchValue, setInitialSearchValue] = useState<string>('');
  const [initialSearchField, setInitialSearchField] = useState<'code' | 'name'>('code');
  const [hasLoaded, setHasLoaded] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    article: Article
  ) => {
    event.stopPropagation();
    setForEditing(article);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin &&
        !user.privileges.readProduct &&
        !user.privileges.writeProduct)
    ) {
      goToWelcome();
    } else {
      resetOptions();
    }
  }, [user, goToWelcome, resetOptions]);

  // Efecto separado para manejar parámetros de búsqueda automática y articleId
  useEffect(() => {
    if (companyId && location.search) {
      // Verificar si hay parámetros de búsqueda en la URL
      const searchParams = new URLSearchParams(location.search);
      const articleId = searchParams.get('articleId');
      const code = searchParams.get('code');
      
      // Si hay articleId, buscar el artículo en la lista actual o por código
      if (articleId) {
        const articleIdNum = parseInt(articleId, 10);
        if (!isNaN(articleIdNum)) {
          // Primero intentar encontrar el artículo en la lista actual
          const foundArticle = articles.find(a => a.id === articleIdNum);
          if (foundArticle && foundArticle.code) {
            setArticle(foundArticle);
            // Redirigir con el código en la URL para asegurar que se cargue
            setTimeout(() => {
              navigate(`/dashboard/article/edit?code=${foundArticle.code}`);
            }, 200);
            // Limpiar la URL actual
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('articleId');
            window.history.replaceState({}, '', newUrl.toString());
            return;
          }
          // Si no está en la lista, necesitamos el código para buscarlo
          // Por ahora, mostrar un mensaje o redirigir a la lista
        }
      }
      
      // Si hay code, cargar el artículo y redirigir a editar
      if (code) {
        getArticle(
          companyId, 
          { code }, 
          (fetchedArticle) => {
            if (fetchedArticle) {
              // Establecer el artículo en Redux primero
              setArticle(fetchedArticle);
              // Redirigir a la página de edición pasando el código como parámetro
              // Esto asegura que si el artículo no está en Redux, se pueda cargar desde la URL
              setTimeout(() => {
                navigate(`/dashboard/article/edit?code=${code}`);
              }, 200);
            } else {
              Swal.fire({
                title: 'Error',
                text: `No se encontró un artículo con el código ${code}`,
                icon: 'error',
                confirmButtonText: 'Aceptar',
              });
            }
          },
          () => {
            Swal.fire({
              title: 'Error',
              text: `Error al buscar el artículo con código ${code}`,
              icon: 'error',
              confirmButtonText: 'Aceptar',
            });
          }
        );
        return;
      }
      
      // Manejar búsqueda automática (código existente)
      const searchTerm = searchParams.get('search');
      const searchField = searchParams.get('field') as 'code' | 'name' | undefined;
      
      if (searchTerm && searchField) {
        console.log('Aplicando búsqueda automática:', { searchTerm, searchField });
        
        // Guardar los valores para pasarlos al Header
        setInitialSearchValue(searchTerm);
        setInitialSearchField(searchField);
        
        // Aplicar búsqueda automática
        setOptions({ [searchField]: searchTerm });
        
        // Limpiar la URL después de aplicar la búsqueda
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('search');
        newUrl.searchParams.delete('field');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, [companyId, location.search, setOptions, getArticle, setArticle, goToEditArticle, articles]);

  useEffect(() => {
    if (companyId) {
      // Resetear el estado de carga cuando cambien los filtros
      setHasLoaded(false);
      getArticles(companyId, options, () => {
        // onSuccess callback - cuando se cargan los artículos
        setHasLoaded(true);
      }, () => {
        // onFailure callback - también marcamos como cargado para mostrar el mensaje de error o vacío
        setHasLoaded(true);
      });
    }
  }, [getArticles, companyId, options]);

  // Cargar datos maestros
  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'product');
      getCategories(companyId, 'product');
    }
  }, [companyId, getTypes, getCategories]);

  const selectArticle = useCallback(
    (article: Article) => {
      clearError();
      setArticle(article);
      goToEditArticle();
    },
    [clearError, goToEditArticle, setArticle]
  );

  const duplicateArticle = useCallback(() => {
    if (forEditing) {
      clearError();
      setArticle({
        ...forEditing,
        code: '',
        alternative_code: forEditing.code,
      });
      goToAddArticle(true);
    }
  }, [clearError, forEditing, goToAddArticle, setArticle]);

  const print = useCallback(
    (ev: any, article: Article) => {
      ev.stopPropagation();
      printArticle(article);
    },
    [printArticle]
  );

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'Sin fecha';
    const date = new Date(timestamp * 1000);
    if (Number.isNaN(date.getTime())) {
      return 'Sin fecha';
    }
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Page
      header={
        <Header 
          initialSearch={initialSearchValue || options.code || ''}
          initialField={initialSearchField}
          productTypes={productTypes}
          productCategories={productCategories}
        />
      }
      content={
        !hasLoaded ? (
          <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
            <CircularProgress />
          </div>
        ) : !articles.length ? (
          <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
            <Typography variant="h6" color="text.secondary">
              No hay artículos
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-24 p-24">
              {articles.map((article) => (
                <Paper
                  key={article.id}
                  className="flex flex-col cursor-pointer hover:shadow-lg transition-shadow duration-200 relative"
                  onClick={() => selectArticle(article)}
                >
                  <IconButton
                    id="basic-menu"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    className="absolute top-4 right-4"
                    onClick={(ev) => handleClick(ev, article)}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  <div className="flex flex-col flex-auto w-full p-12 items-center text-center">
                    <img
                      className="w-200 h-200 rounded-xl object-cover"
                      src={
                        article.files.length
                          ? article.files[0].URL
                          : 'https://placehold.co/200x200'
                      }
                      alt={article.name}
                    />
                    <Typography className="mt-24 font-medium text-slate-950">
                      {article.name} / {article.brand}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Creado: {formatDate(article.created_at)}
                    </Typography>
                  </div>
                  <div className="mb-12 px-12 container-barcode text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Typography className="text-sm text-gray-500 text-center">
                        Código de barras
                      </Typography>
                      {article.code && (
                        <CopyButton
                          textToCopy={article.code}
                          tooltip="Copiar código"
                        />
                      )}
                    </div>
                    {article.code && (
                      <Barcode
                        value={article.code}
                        fontSize={16}
                        height={50}
                        format="CODE128"
                        margin={0}
                        displayValue={true}
                        background="#FFFFFF"
                        textAlign="center"
                      />
                    )}
                  </div>
                  <Button
                    className="mx-4 my-4 w-100"
                    color="secondary"
                    onClick={(ev) => print(ev, article)}
                  >
                    <PrintIcon className="mx-2" />
                    Imprimir etiqueta
                  </Button>
                </Paper>
              ))}
            </div>
            {!noMore ? (
              <div
                className="text-center underline cursor-pointer my-24"
                onClick={increasePage}
              >
                Mostras más
              </div>
            ) : null}
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem
                onClick={duplicateArticle}
                className="flex items-center gap-12"
              >
                <ContentCopyIcon fontSize="small" />
                <div>Replicar</div>
              </MenuItem>
            </Menu>
          </div>
        )
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
};

export default Articles;
